/**
 * Per-window vault scope plumbing.
 *
 * Kept dependency-free so it can be unit-tested directly without
 * dragging the @vultisig/sdk ESM module into Jest. `vultisig.js`
 * imports from here and layers the higher-level behaviour
 * (`setActiveVault`, `getLoadedVaultCount`, …) on top.
 *
 * Each in-app window (Money Manager, a wizard, …) has its OWN
 * scope keyed by `windowId`. Wizards launched from a launcher get
 * a `registerWindowParent(childId, parentId)` link so they can
 * still see the launcher's loaded vault — but unrelated top-level
 * windows are isolated by default. The earlier implementation
 * searched every scope as a fallback, which produced the
 * "I opened the Receive wizard from the Privacy Suite and it
 * already had a vault from a previous session" bug.
 */

const _vaultScopes = new Map();
const _parentLinks = new Map(); // child windowId -> parent windowId
const DEFAULT_SCOPE = '__default__';

/**
 * Resolve (and lazily create) the scope record for a windowId. Pass
 * `null` / `undefined` to get the default global bucket — useful for
 * functions that need somewhere to put SDK-level callbacks before a
 * UI window has mounted.
 */
export function getScope(windowId) {
	const id = windowId || DEFAULT_SCOPE;
	let scope = _vaultScopes.get(id);
	if (!scope) {
		scope = { activeVault: null, additionalVaults: [], signingModalCallback: null };
		_vaultScopes.set(id, scope);
	}
	return scope;
}

/**
 * Drop a window's scope record and detach any sub-windows that
 * pointed at it. Call this when the owning window unmounts (the
 * WindowManager close handler wires this up automatically).
 */
export function clearVaultScope(windowId) {
	if (!windowId) return;
	_vaultScopes.delete(windowId);
	_parentLinks.delete(windowId);
	for (const [childId, parentId] of _parentLinks) {
		if (parentId === windowId) _parentLinks.delete(childId);
	}
}

export function getVaultScopeIds() {
	return [..._vaultScopes.keys()].filter((k) => k !== DEFAULT_SCOPE);
}

/**
 * Register a launcher → sub-window relationship so the sub-window's
 * vault lookups can see the launcher's loaded vault. Wizards that
 * are NOT explicitly registered are isolated — they will never
 * silently inherit a vault from an unrelated top-level window.
 */
export function registerWindowParent(childWindowId, parentWindowId) {
	if (!childWindowId || !parentWindowId) return;
	if (childWindowId === parentWindowId) return;
	_parentLinks.set(childWindowId, parentWindowId);
}

export function unregisterWindowParent(childWindowId) {
	if (childWindowId) _parentLinks.delete(childWindowId);
}

/**
 * Find an active vault by walking the parent chain — strictly the
 * preferred scope first, then any registered parent, and so on. A
 * windowId without registered ancestors never falls through to other
 * top-level scopes, which is what users expect when they open a
 * standalone wizard from the Privacy Suite or the desktop.
 *
 * Returns `{ vault, windowId }` even when no vault is found — the
 * windowId is the originally-requested one so callers can `setScope`
 * on it without re-deriving the id.
 */
export function findActiveVault(preferredWindowId) {
	const seen = new Set();
	let cur = preferredWindowId || DEFAULT_SCOPE;
	while (cur && !seen.has(cur)) {
		seen.add(cur);
		const scope = _vaultScopes.get(cur);
		if (scope?.activeVault) {
			return { vault: scope.activeVault, windowId: cur };
		}
		cur = _parentLinks.get(cur);
	}
	return { vault: null, windowId: preferredWindowId };
}

// Test helper — never call from production code.
export function __resetAllVaultScopesForTests() {
	_vaultScopes.clear();
	_parentLinks.clear();
}
